import React, { useState, useEffect, useMemo } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line 
} from 'recharts';
import { Filter, TrendingUp, CheckCircle, FileText, Download, Printer, PieChart as PieIcon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import { roomLineTotal, offerDiscount, offerNetFactor, discountLabel, formatMoneyTR } from '../../utils/pricing';
import { useSeason } from '../../context/SeasonContext';
import { offerInSeason } from '../../utils/season';
import { API_URL, readJsonOrThrow } from '../../utils/api';
import { dateKey, todayKey, offerRoomsText } from '../../utils/offerShare';

const MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

// Tarihler İstanbul gününe göre ('YYYY-MM-DD', dateKey). Ham ISO metni ("2026-12-24T21:00:00.000Z") bir gün geri
// kaydığı için ne karşılaştırmada ne de Excel'de kullanılır.
const keyYear = (key) => Number(key.slice(0, 4));
const keyMonth = (key) => Number(key.slice(5, 7)); // 1-12
const keyDay = (key) => Number(key.slice(8, 10));
// 'YYYY-MM-DD' → '25.12.2026' (Excel'in Türkçe tarih biçimi)
const keyToTR = (key) => (key ? `${key.slice(8, 10)}.${key.slice(5, 7)}.${key.slice(0, 4)}` : '');
// CSV hücresi: çift tırnak kaçışı (müşteri adındaki " satırı bozmasın)
const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

export default function Reports() {
  const { currentUser } = useAuth();
  const { data, fetchData } = useData();
  const { selectedSeason } = useSeason();
  const { darkMode } = useTheme();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [timeFrame, setTimeFrame] = useState('monthly'); // 'monthly' | 'yearly'
  const [selectedYear, setSelectedYear] = useState(() => keyYear(todayKey()));
  const [selectedMonth, setSelectedMonth] = useState(() => keyMonth(todayKey()) - 1); // 0-11

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  const canViewFinancials = currentUser?.permissions?.view_financials;

  useEffect(() => {
    let cancelled = false;
    fetchData('roomTypes');
    const fetchOffers = async () => {
      try {
        const list = await readJsonOrThrow(await fetch(`${API_URL}/offers`), 'Teklifler yüklenemedi.');
        if (!Array.isArray(list)) throw new Error('Teklifler yüklenemedi.');
        // Kayıt günü (İstanbul). Tarihi okunamayan kayıt '' kalır ve hiçbir döneme yazılmaz ("bugün" yedeği yok).
        if (!cancelled) setOffers(list.map(offer => ({ ...offer, createdKey: dateKey(offer.createdAt) })));
      } catch (error) {
        console.error("Veri çekme hatası:", error);
        if (!cancelled) setLoadError(error.message || 'Teklifler yüklenemedi.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOffers();
    return () => { cancelled = true; };
  }, [fetchData]);

  // Yıl seçenekleri: tekliflerin kayıt yılları + içinde bulunulan yıl (eskiden sabit 2024-2027 idi)
  const yearOptions = useMemo(() => {
    const years = new Set([keyYear(todayKey())]);
    offers.forEach(o => { if (o.createdKey) years.add(keyYear(o.createdKey)); });
    return [...years].sort((a, b) => a - b);
  }, [offers]);

  // Verileri İşleme ve Filtreleme
  const processedData = useMemo(() => {
    if (loading) return null;

    const year = Number(selectedYear);
    const month = Number(selectedMonth) + 1; // 1-12

    // 1. Sezon (üstteki seçici) + tarih filtreleme (kayıt günü, İstanbul)
    const filteredOffers = offers.filter(offer => {
      if (!offerInSeason(offer, selectedSeason)) return false;
      const key = offer.createdKey;
      if (!key) return false;
      if (timeFrame === 'monthly') {
        return keyYear(key) === year && keyMonth(key) === month;
      }
      return keyYear(key) === year;
    });

    // 2. İstatistikler
    const totalRevenue = filteredOffers
      .filter(o => o.status === 'Onaylandı')
      .reduce((acc, curr) => acc + (Number(curr.totalPrice) || 0), 0);
    
    const totalOffers = filteredOffers.length;
    const approvedOffers = filteredOffers.filter(o => o.status === 'Onaylandı').length;
    const conversionRate = totalOffers > 0 ? ((approvedOffers / totalOffers) * 100).toFixed(1) : 0;

    // 3. Durum Dağılımı (Pie Chart)
    const statusCounts = {};
    filteredOffers.forEach(o => {
      const status = o.status || 'Bilinmiyor';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
    const statusData = Object.keys(statusCounts).map(key => ({ name: key, value: statusCounts[key] }));

    // 4. Oda Tipine Göre Ciro (Bar Chart)
    const roomRevenue = {};
    
    // Güncel oda isimlerini ID'ye göre haritala
    const roomNames = {};
    if (Array.isArray(data.roomTypes)) {
        data.roomTypes.forEach(rt => {
            roomNames[rt.id] = rt.name;
            roomRevenue[rt.name] = 0; // Tüm odaları başlangıçta 0 olarak ekle
        });
    }

    filteredOffers.forEach(o => {
      if (o.status === 'Onaylandı') {
        // Çoklu oda desteği
        if (o.rooms && Array.isArray(o.rooms)) {
            const netFactor = offerNetFactor(o); // teklif indirimi odalara oranla dağıtılır
            o.rooms.forEach(room => {
                const rName = roomNames[room.id] || room.name || 'Bilinmiyor';
                const rTotal = roomLineTotal(room) * netFactor;
                roomRevenue[rName] = (roomRevenue[rName] || 0) + rTotal;
            });
        } else if (o.room) {
            // Eski veri yapısı veya tek oda
            const rName = (o.room.id && roomNames[o.room.id]) || o.room.name || 'Bilinmiyor';
            const rTotal = Number(o.totalPrice) || 0; // Tek odada toplam fiyatı alıyoruz
            roomRevenue[rName] = (roomRevenue[rName] || 0) + rTotal;
        }
      }
    });
    const roomData = Object.keys(roomRevenue).map(key => ({ name: key, Ciro: roomRevenue[key] }));

    // 5. Zaman Çizelgesi (Line Chart)
    const timelineMap = {};
    filteredOffers.forEach(o => {
        if (o.status === 'Onaylandı') {
            const key = timeFrame === 'monthly'
                ? keyDay(o.createdKey)                // Günlük
                : MONTHS[keyMonth(o.createdKey) - 1]; // Aylık
            timelineMap[key] = (timelineMap[key] || 0) + (Number(o.totalPrice) || 0);
        }
    });

    let timelineData = [];
    if (timeFrame === 'monthly') {
        // Ayın günlerini doldur (month 1-12 → bir sonraki ayın 0. günü = bu ayın son günü)
        const daysInMonth = new Date(year, month, 0).getDate();
        for (let i = 1; i <= daysInMonth; i++) {
            timelineData.push({ name: i.toString(), Ciro: timelineMap[i] || 0 });
        }
    } else {
        // Yılın aylarını doldur
        MONTHS.forEach(monthName => {
            timelineData.push({ name: monthName, Ciro: timelineMap[monthName] || 0 });
        });
    }

    // 6. Kaynak Dağılımı
    const sourceCounts = {};
    filteredOffers.forEach(o => {
      const sourceName = o.source?.name || 'Bilinmiyor';
      sourceCounts[sourceName] = (sourceCounts[sourceName] || 0) + 1;
    });
    const sourceData = Object.keys(sourceCounts).map(key => ({ name: key, value: sourceCounts[key] }));

    return { totalRevenue, totalOffers, approvedOffers, conversionRate, statusData, roomData, timelineData, sourceData, filteredOffers };
  }, [offers, timeFrame, selectedYear, selectedMonth, loading, data.roomTypes, selectedSeason]); // MONTHS artık dışarıda olduğu için bağımlılık değil

  const handleExportExcel = () => {
    if (!processedData || !processedData.filteredOffers || processedData.filteredOffers.length === 0) {
      alert("İndirilecek veri bulunamadı.");
      return;
    }

    // Yeni sütunlar sona eklendi (mevcut Excel şablonlarının sütun sırası bozulmasın).
    // Tarihler İstanbul günü (gg.aa.yyyy); tutarlar yalnızca finansal yetkiyle yazılır (ekrandaki '*** TL' kuralı).
    const headers = ["Müşteri", "Telefon", "Email", "Durum", "Tutar", "Oda", "Giriş", "Çıkış", "Kaynak", "Oluşturulma Tarihi", "İndirim", "İndirim Tutarı"];
    const csvContent = [
      headers.join(","),
      ...processedData.filteredOffers.map(offer => {
        const discount = offerDiscount(offer);
        return [
          offer.customerName || '',
          offer.phone || '',
          offer.email || '',
          offer.status || '',
          canViewFinancials ? (Number(offer.totalPrice) || 0) : '***',
          offerRoomsText(offer),
          keyToTR(dateKey(offer.checkIn)),
          keyToTR(dateKey(offer.checkOut)),
          offer.source?.name || '',
          keyToTR(offer.createdKey),
          discount.amount > 0 ? discountLabel(discount) : '',
          discount.amount > 0 ? (canViewFinancials ? discount.amount : '***') : ''
        ].map(csvCell).join(",");
      })
    ].join("\n");

    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Rapor_${timeFrame}_${selectedYear}${timeFrame === 'monthly' ? '_' + (parseInt(selectedMonth) + 1) : ''}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <LoadingSpinner />;

  if (!currentUser?.permissions?.view_reports) return <div className="p-10 text-center text-red-500 dark:text-red-400">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</div>;

  return (
    <div className="print:p-0 print:max-w-none print:ml-0">
      <style>{`
        @media print {
          @page { margin: 1cm; size: landscape; }
          body { -webkit-print-color-adjust: exact; }
          /* Sidebar boşluğunu sıfırla (Eğer layout'ta margin-left varsa) */
          main, #root > div > div { margin-left: 0 !important; padding: 0 !important; }
        }
      `}</style>
      <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Raporlar ve Analizler</h1>
        
        {/* Filtreler */}
        <div className="flex flex-wrap gap-3 bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm print:hidden border border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2">
                <Filter size={18} className="text-gray-500 dark:text-gray-400" />
                <select 
                    className="p-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={timeFrame}
                    onChange={(e) => setTimeFrame(e.target.value)}
                >
                    <option value="monthly">Aylık</option>
                    <option value="yearly">Yıllık</option>
                </select>
            </div>

            <select 
                className="p-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
            >
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>

            {timeFrame === 'monthly' && (
                <select 
                    className="p-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                >
                    {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
                </select>
            )}

            <button 
                onClick={() => window.print()}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors text-sm ml-auto"
            >
                <Printer size={18} />
                Yazdır
            </button>

            <button 
                onClick={handleExportExcel}
                className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 transition-colors text-sm"
            >
                <Download size={18} />
                Excel İndir
            </button>
        </div>
      </div>

      {loadError && (
        <div className="mb-6 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300 print:hidden">
          {loadError}
        </div>
      )}

      {/* Özet Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow border border-gray-100 dark:border-gray-700">
            <div className="flex justify-between items-start">
                <div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Toplam Ciro (Onaylanan)</p>
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-white mt-1">{canViewFinancials ? formatMoneyTR(processedData.totalRevenue) + ' TL' : '*** TL'}</h3>
                </div>
                <div className="p-3 bg-green-100 rounded-lg text-green-600 dark:bg-green-900/30 dark:text-green-400"><TrendingUp size={24} /></div>
            </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow border border-gray-100 dark:border-gray-700">
            <div className="flex justify-between items-start">
                <div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Toplam Teklif</p>
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-white mt-1">{processedData.totalOffers}</h3>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"><FileText size={24} /></div>
            </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow border border-gray-100 dark:border-gray-700">
            <div className="flex justify-between items-start">
                <div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Onaylanan Teklif</p>
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-white mt-1">{processedData.approvedOffers}</h3>
                </div>
                <div className="p-3 bg-indigo-100 rounded-lg text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400"><CheckCircle size={24} /></div>
            </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow border border-gray-100 dark:border-gray-700">
            <div className="flex justify-between items-start">
                <div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Dönüşüm Oranı</p>
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-white mt-1">%{processedData.conversionRate}</h3>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg text-orange-600 dark:bg-orange-900/30 dark:text-orange-400"><PieIcon size={24} /></div>
            </div>
        </div>
      </div>

      {/* Grafikler */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Zaman Çizelgesi */}
        {canViewFinancials && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow border border-gray-100 dark:border-gray-700 lg:col-span-2">
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200 mb-6">Zaman İçinde Ciro ({timeFrame === 'monthly' ? 'Günlük' : 'Aylık'})</h3>
            <div className="h-80 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={processedData.timelineData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#374151" : "#e5e7eb"} />
                        <XAxis dataKey="name" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip
                            formatter={(value) => `${formatMoneyTR(value)} TL`}
                            contentStyle={{ backgroundColor: darkMode ? '#1F2937' : '#fff', borderRadius: '8px', border: darkMode ? '1px solid #374151' : '1px solid #e5e7eb', color: darkMode ? '#fff' : '#000' }}
                            itemStyle={{ color: darkMode ? '#fff' : '#000' }}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="Ciro" stroke="#8884d8" strokeWidth={3} activeDot={{ r: 8 }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
        )}

        {/* Oda Tipine Göre Ciro */}
        {canViewFinancials && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow border border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200 mb-6">Oda Tipine Göre Ciro</h3>
            <div className="h-80 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={processedData.roomData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#374151" : "#e5e7eb"} />
                        <XAxis type="number" stroke="#9ca3af" />
                        <YAxis dataKey="name" type="category" width={100} stroke="#9ca3af" />
                        <Tooltip
                            formatter={(value) => `${formatMoneyTR(value)} TL`}
                            contentStyle={{ backgroundColor: darkMode ? '#1F2937' : '#fff', borderRadius: '8px', border: darkMode ? '1px solid #374151' : '1px solid #e5e7eb', color: darkMode ? '#fff' : '#000' }}
                            itemStyle={{ color: darkMode ? '#fff' : '#000' }}
                        />
                        <Legend />
                        <Bar dataKey="Ciro" fill="#82ca9d" radius={[0, 4, 4, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
        )}

        {/* Teklif Durum Dağılımı */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow border border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200 mb-6">Teklif Durum Dağılımı</h3>
            <div className="h-80 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={processedData.statusData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            fill="#8884d8"
                            paddingAngle={5}
                            dataKey="value"
                            label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                            {processedData.statusData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>

        {/* Teklif Kaynak Dağılımı */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow border border-gray-100 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200 mb-6">Teklif Kaynak Dağılımı</h3>
            <div className="h-80 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={processedData.sourceData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            fill="#82ca9d"
                            paddingAngle={5}
                            dataKey="value"
                            label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                            {processedData.sourceData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>

      </div>
      </div>
    </div>
  );
}
